/**
 * Unified egress-audit model. Collapses HTTP request decisions and stateful-TCP connection
 * lifecycle events into a single protocol-agnostic list, organized by the egress DECISION
 * (allowed vs denied). Every egress attempt — an HTTP request or a TCP connection — is either
 * allowed or denied by policy, so both render in the same Allowed / Denied buckets with a
 * per-entry protocol badge, rather than in separate per-protocol sections.
 *
 * @module components/connectors/unifiedEgress
 */

import type { EgressDecisionEntry, StatefulTcpEgressEntry } from '@/types';

export type EgressProtocol = 'http' | 'azuresql' | 'postgresql' | 'redis' | 'tcp';

export type EgressStatus = 'allowed' | 'denied';

/** Derived connection state for a stateful-TCP entry. */
export type ConnectionState = 'open' | 'closed' | 'failed';

interface UnifiedEgressEntryBase {
  /** Stable key for React lists. */
  id: string;
  /** Which policy-decision bucket this entry belongs to. */
  status: EgressStatus;
  /** Most-recent activity time (epoch ms) for sorting newest-first. */
  timestampMs: number;
  /** Protocol discriminator used for the badge. */
  protocol: EgressProtocol;
  /** Friendly protocol/connector label (e.g. "HTTP", "Azure SQL"). */
  protocolLabel: string;
}

export interface UnifiedHttpEntry extends UnifiedEgressEntryBase {
  kind: 'http';
  protocol: 'http';
  method: string;
  /** Full request URL (scheme://host/path). */
  url: string;
  matchedRule?: string;
  connectionId?: string;
  connectionName?: string;
}

export interface UnifiedConnectionEntry extends UnifiedEgressEntryBase {
  kind: 'connection';
  /** "server:port" (or just server when no port). */
  endpoint: string;
  database?: string;
  /** Proxy login name — already masked by the API. */
  proxyLoginName?: string;
  sourceIp?: string;
  connState: ConnectionState;
  /** Raw outcome string (Connected, PolicyDenied, LoginFailed, ...). */
  outcome?: string;
  failureReason?: string;
  /** Whether byte/duration metrics are final (a Closed phase exists). */
  metricsAreFinal: boolean;
  bytesIn?: number;
  bytesOut?: number;
  durationMs?: number;
  startedAtMs?: number;
}

export type UnifiedEgressEntry = UnifiedHttpEntry | UnifiedConnectionEntry;

export interface UnifiedEgress {
  allowed: UnifiedEgressEntry[];
  denied: UnifiedEgressEntry[];
  /** True when there are no entries in either bucket (drives the empty state). */
  isEmpty: boolean;
}

interface EgressDecisionsShape {
  networkEgress?: { allowed?: EgressDecisionEntry[]; denied?: EgressDecisionEntry[] };
  statefulTcp?: { connections?: StatefulTcpEgressEntry[] };
}

/** Maps a raw connector type to a protocol id + friendly label. */
function connectorInfo(connectorType?: string): { protocol: EgressProtocol; label: string } {
  switch (connectorType) {
    case 'AzureSql':
      return { protocol: 'azuresql', label: 'Azure SQL' };
    case 'PostgreSql':
      return { protocol: 'postgresql', label: 'PostgreSQL' };
    case 'Redis':
      return { protocol: 'redis', label: 'Redis' };
    default:
      return { protocol: 'tcp', label: connectorType || 'TCP' };
  }
}

/** Only an explicit policy denial lands a connection in the Denied bucket. */
function isPolicyDenied(outcome?: string): boolean {
  return outcome === 'PolicyDenied' || outcome === 'ResolverDenied';
}

/** A non-"Connected" outcome means the connection ultimately failed (still egress-allowed). */
function isFailureOutcome(outcome?: string): boolean {
  return !!outcome && outcome.toLowerCase() !== 'connected';
}

export function toMs(timestamp?: string): number {
  if (!timestamp) return 0;
  const ms = Date.parse(timestamp);
  return Number.isNaN(ms) ? 0 : ms;
}

/**
 * Azure SQL Redirect gateway-artifact detector. Redirect makes the client open TWO connections
 * (gateway :1433, then a redirected worker node on a high port). The gateway hop is a routing
 * artifact: it never relays (no Established phase), transfers no bytes, and still "succeeds"
 * (Connected). We identify it by exactly that signature — a Closed-only, zero-byte, Connected
 * group — rather than by inspecting the recorded server, so it works whether the audited server is
 * an IP or a hostname. Both this unified audit view and StatefulConnectionsSection use this
 * predicate so they consolidate the two hops identically — otherwise the same underlying audit
 * entries render differently across the two surfaces.
 * NOTE: interim heuristic; the correct fix is for the proxy (SCRAP) to correlate the two hops.
 */
export function isRedirectGatewayArtifact(args: {
  hasEstablished: boolean;
  hasClosed: boolean;
  outcome?: string;
  bytesIn: number;
  bytesOut: number;
}): boolean {
  return (
    !args.hasEstablished &&
    args.hasClosed &&
    args.outcome === 'Connected' &&
    args.bytesIn === 0 &&
    args.bytesOut === 0
  );
}

/**
 * Groups raw phase entries into one record per connection (paired by correlationId) and derives
 * connection state. Established + Closed entries for the same connection collapse into a single
 * record. Byte/duration metrics are final only once a Closed phase exists.
 *
 * Azure SQL Redirect makes the client open TWO connections (gateway :1433 then a redirected
 * worker node on a high port). The gateway hop is a routing artifact — it never relays (no
 * Established phase), transfers no bytes, and its recorded server is an IP. We suppress it so a
 * single logical connection surfaces. NOTE: this is an interim heuristic; the correct fix is for
 * the proxy (SCRAP) to correlate the two hops into one logical connection.
 */
export function aggregateConnections(entries: StatefulTcpEgressEntry[]): UnifiedConnectionEntry[] {
  const groups = new Map<string, StatefulTcpEgressEntry[]>();

  entries.forEach((entry, index) => {
    const key = entry.correlationId || `entry-${index}`;
    const bucket = groups.get(key);
    if (bucket) bucket.push(entry);
    else groups.set(key, [entry]);
  });

  const result: UnifiedConnectionEntry[] = [];

  for (const [id, group] of groups) {
    const established = group.find((e) => e.phase === 'Established');
    const closed = group.find((e) => e.phase === 'Closed');
    const representative = closed ?? established ?? group[0];
    const metricsSource = closed ?? representative;

    const bytesIn = metricsSource.bytesIn ?? 0;
    const bytesOut = metricsSource.bytesOut ?? 0;
    const outcome = representative.outcome;

    // Suppress the Azure SQL Redirect gateway artifact (shared detector, so this view and
    // StatefulConnectionsSection consolidate the two hops identically).
    if (
      isRedirectGatewayArtifact({
        hasEstablished: !!established,
        hasClosed: !!closed,
        outcome,
        bytesIn,
        bytesOut,
      })
    ) {
      continue;
    }

    const { protocol, label } = connectorInfo(representative.connectorType);

    let connState: ConnectionState;
    if (isFailureOutcome(outcome)) connState = 'failed';
    else if (closed) connState = 'closed';
    else if (established) connState = 'open';
    else connState = 'closed';

    const server = representative.server;
    const port = representative.port;
    const endpoint = server ? (port ? `${server}:${port}` : server) : label;

    const lastActivityMs = group.reduce((latest, e) => Math.max(latest, toMs(e.timestamp)), 0);

    result.push({
      kind: 'connection',
      id,
      status: isPolicyDenied(outcome) ? 'denied' : 'allowed',
      timestampMs: lastActivityMs,
      protocol,
      protocolLabel: label,
      endpoint,
      database: representative.database,
      proxyLoginName: representative.proxyLoginName,
      sourceIp: representative.sourceIp,
      connState,
      outcome,
      failureReason: representative.failureReason,
      metricsAreFinal: closed !== undefined,
      bytesIn,
      bytesOut,
      durationMs: metricsSource.durationMs,
      startedAtMs: representative.startedAt ? toMs(representative.startedAt) : undefined,
    });
  }

  return result;
}

function toHttpEntry(
  entry: EgressDecisionEntry,
  status: EgressStatus,
  index: number
): UnifiedHttpEntry {
  const url = `${entry.scheme ?? 'https'}://${entry.host ?? ''}${entry.path ?? ''}`;
  return {
    kind: 'http',
    id: `http-${status}-${index}`,
    status,
    timestampMs: toMs(entry.timestamp),
    protocol: 'http',
    protocolLabel: 'HTTP',
    method: (entry.method ?? 'GET').toUpperCase(),
    url,
    matchedRule: entry.matchedRule,
    connectionId: entry.connectionId,
    connectionName: entry.connectionName,
  };
}

/**
 * Builds the unified allowed / denied egress lists from the raw decisions object, merging HTTP
 * request decisions and (redirect-consolidated) stateful-TCP connections, each sorted newest-first.
 */
export function buildUnifiedEgress(egressDecisions?: EgressDecisionsShape): UnifiedEgress {
  const httpAllowed = (egressDecisions?.networkEgress?.allowed ?? []).map((e, i) =>
    toHttpEntry(e, 'allowed', i)
  );
  const httpDenied = (egressDecisions?.networkEgress?.denied ?? []).map((e, i) =>
    toHttpEntry(e, 'denied', i)
  );

  const connections = aggregateConnections(egressDecisions?.statefulTcp?.connections ?? []);

  const allowed: UnifiedEgressEntry[] = [
    ...httpAllowed,
    ...connections.filter((c) => c.status === 'allowed'),
  ].sort((a, b) => b.timestampMs - a.timestampMs);

  const denied: UnifiedEgressEntry[] = [
    ...httpDenied,
    ...connections.filter((c) => c.status === 'denied'),
  ].sort((a, b) => b.timestampMs - a.timestampMs);

  return { allowed, denied, isEmpty: allowed.length === 0 && denied.length === 0 };
}
